/**
 * Formatting and Validation Helpers for Data Sanitation
 */
import { LPL_DELIVERING_FIRM_NAME } from "../constants/deliveringFirms";

/** Parse to integer, preserving null for empty/invalid input so untouched fields stay pending. */
export const toIntOrNull = (val) => {
  if (val === "" || val === null || val === undefined) return null;
  const num = parseInt(val, 10);
  return isNaN(num) ? null : num;
};

// SSN/TIN helpers (re-used from HouseholdContent logic)
export const formatSSNInput = (digits) => {
  if (typeof digits === "string" && digits.includes("*")) {
    return digits;
  }
  const safe = digits == null ? "" : String(digits);
  const d = safe.replace(/\D/g, "").substring(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

export const validateSSNDigits = (digits) => {
  if (typeof digits !== 'string') return false;
  if (digits.includes('*')) {
    const d = digits.replace(/[^\d*]/g, "");
    return d.length === 9;
  }
  const d = digits.replace(/\D/g, "");
  if (d.length !== 9) return false;
  const area = parseInt(d.substring(0, 3), 10);
  if (area === 0) return false;
  return true;
};

export const countSSNDigits = (value) => {
  const str = String(value ?? "");
  const cleaned = str.includes("*")
    ? str.replace(/[^\d*]/g, "")
    : str.replace(/\D/g, "");
  return cleaned.length;
};


export const getTaxIdCountError = (value) => {
  if (value == null || String(value).trim() === "") return null;
  if (countSSNDigits(value) !== 9) return "Tax ID must be 9 digits.";
  return null;
};

// Phone helpers (Standard US format: XXX-XXX-XXXX)
export const formatPhoneInput = (digits) => {
  const d = digits.replace(/\D/g, "").substring(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

export const validatePhoneDigits = (digits) => {
  const d = digits.replace(/\D/g, "");
  return d.length === 10;
};

// Zip Code helpers (5 digits)
export const formatZipInput = (digits) => {
  return digits.replace(/\D/g, "").substring(0, 5);
};

export const validateZipDigits = (digits) => {
  const d = digits.replace(/\D/g, "");
  return d.length === 5;
};

export const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
};

// Numeric Input handler (Currency, Percentages, etc.)
export const formatNumericInput = (value, allowDecimal = true) => {
  if (typeof value !== 'string') return value;
  
  let val = value;
  
  // Strip non-numeric and non-decimal characters
  if (allowDecimal) {
    val = val.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
  } else {
    val = val.replace(/\D/g, '');
  }

  // Prevent leading zeros unless it's "0."
  if (val.length > 1 && val.startsWith('0') && !val.startsWith('0.')) {
     val = val.replace(/^0+(?=\d)/, '');
  }

  return val;
};

// Routing Number helper (exactly 9 digits, leading zeros preserved)
export const formatRoutingNumberInput = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\D/g, '').substring(0, 9);
};

const isDigit = (ch) => ch >= '0' && ch <= '9';

export const validateRoutingNumber = (value) => {
  const d = (value || '').replace(/\D/g, '');
  if (d.length !== 9) return false;
  if (![...d].every(isDigit)) return false;
  const n = [...d].map((ch) => parseInt(ch, 10));
  const checksum =
    3 * (n[0] + n[3] + n[6]) +
    7 * (n[1] + n[4] + n[7]) +
    (n[2] + n[5] + n[8]);
  return checksum % 10 === 0;
};

export const validateLplAccountNumber = (accountNumber, deliveringFirm) => {
  const firm = String(deliveringFirm ?? "").trim().toUpperCase();
  if (firm !== LPL_DELIVERING_FIRM_NAME.toUpperCase()) {
    return { valid: true, error: null };
  }
  const value = String(accountNumber ?? "").trim();
  if (!/^\d{8}$/.test(value)) {
    return { valid: false, error: "LPL account number must be exactly 8 digits" };
  }
  return { valid: true, error: null };
};

// Account Number helper (alphanumeric; leading zeros preserved)
export const formatAccountNumberInput = (value, maxLen = 30) => {
  if (typeof value !== "string") return value;
  const cleaned = value.replace(/[^0-9a-z]/gi, "");
  return maxLen ? cleaned.substring(0, maxLen) : cleaned;
};

// Payload string cleaner
export const stripSpecialChars = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\D/g, '');
};
